import { useCallback, useEffect, useRef, useState } from "react";
import { wait } from "utils/utils";

const useScroll = ({ elementRef }) => {
  const [scrollMetrics, setScrollMetrics] = useState({
    height: 0,
    top: 0,
    bottom: 0,
  });
  const isScrolling = useRef(false);

  const scrollToStart = useCallback(
    async ({ start = "bottom", onScrollDone }) => {
      if (isScrolling.current) {
        return;
      }

      // make sure all is rendered
      await wait(100);

      const top = start === "top" ? 0 : scrollMetrics.height + 2000;

      elementRef?.current.scroll({ top, behavior: "smooth" });

      if (typeof onScrollDone === "function") {
        onScrollDone();
      }
    },
    [elementRef, scrollMetrics?.height]
  );

  const handleScroll = useCallback(
    ({ target: { scrollHeight, scrollTop, clientHeight } }) => {
      clearTimeout(window.scrollEndTimer);

      if (!isScrolling.current) {
        isScrolling.current = true;
      }

      window.scrollEndTimer = setTimeout(() => {
        setScrollMetrics({
          height: scrollHeight,
          top: scrollTop,
          bottom: scrollHeight - scrollTop - clientHeight,
        });
        isScrolling.current = false;
      }, 100);
    },
    []
  );

  useEffect(() => {
    const wrapperElement = elementRef?.current;
    wrapperElement.addEventListener("scroll", handleScroll);

    return () => wrapperElement.removeEventListener("scroll", handleScroll);
  }, [elementRef, handleScroll]);

  return {
    isScrolling: isScrolling.current,
    scrollToStart,
    scrollHeight: scrollMetrics.height,
    scrollTop: scrollMetrics.top,
    scrollBottom: scrollMetrics.bottom,
  };
};

export { useScroll };
